import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { StepService } from '@lx/quote-tool/shared/services/step-service';
import { FlywireCheckout, FlywireGatewayV2Service, PAYMENT_GATEWAY_V2_SERVICE } from '@lx/shared/payment';
import { MetaService } from '@lx/shared/seo';
import { MetaDefinition } from '@angular/platform-browser';
import { LoggerService } from '@lx/shared/logger';

const flywireContainerId = 'flywire-container';

@Component({
  selector: 'lx-enrolment-payment',
  templateUrl: './flywire-payment.component.html',
  styleUrls: ['./flywire-payment.component.scss'],
  providers: [{ provide: PAYMENT_GATEWAY_V2_SERVICE, useClass: FlywireGatewayV2Service }],
})
export class FlywirePaymentComponent implements OnInit, OnDestroy {
  flywireCheckoutLoaded = false;
  private validStep$ = new BehaviorSubject(true);
  private subscription = new Subscription();

  private readonly paymentGatewayService = inject(PAYMENT_GATEWAY_V2_SERVICE);
  private readonly stepService = inject(StepService);
  private readonly metaService = inject(MetaService);
  private readonly logger = inject(LoggerService);

  constructor() {
    this.metaService.removeMetaTagOnRouteChange('robots');
  }

  ngOnInit(): void {
    const robots: MetaDefinition = { name: 'robots', content: 'noindex, nofollow' };
    this.metaService.addMetaTag(robots);
    this.stepService.setStepValid(this.validStep$);

    // Initialize Flywire payment gateway V2 and set up payment process using the preload approach to enhance user experience.
    this.paymentGatewayService.bootstrapV2();
    this.subscription.add(this.paymentGatewayService.scriptV2LoadedSubject.subscribe(this.payWithPreloadApproach()));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Sets up the payment process using the preload approach to enhance user experience.
   */
  private payWithPreloadApproach() {
    // Return a function to be called when the Flywire V2 script is loaded for preloading the payment checkout.
    return () => {
      this.paymentGatewayService
        .preloadPayment(this.stepService.getFlywirePaymentId(), flywireContainerId)
        .then((checkout) => {
          // Mark the checkout as loaded to update the UI accordingly.
          this.flywireCheckoutLoaded = true;
          this.makePayment(checkout);
        })
        .catch((e) => {
          this.logger.error('Error during payment preloading:', e);
          this.stepService.navigateToPreviousStep();
        });
    };
  }

  /**
   * Initiates the payment process using the preloaded Flywire checkout instance.
   * @param checkout The preloaded Flywire checkout instance.
   */
  private makePayment(checkout: FlywireCheckout) {
    this.paymentGatewayService
      .mount(checkout, flywireContainerId)
      .then((result) => {
        if (result.success) {
          this.stepService.navigateToNextStep();
        }
      })
      .catch((e) => {
        if (e.errors[0].type === 'cancellation') {
          return;
        }
        this.logger.error('Error during payment:', e);
        this.stepService.navigateToPreviousStep();
      });
  }
}
